import React from 'react';
import {FormattedMessage} from 'react-intl';
import {FieldArray, FormikProvider} from 'formik';
import classnames from 'classnames';
import {useCustomerDetailsUIContext} from '../CustomerDetailsUIContext';
import EditableRow from '../EditableRow';
import {gridTypes as G} from '../../constants/gridTypes';
import {
  headerCellsArrayCommonDocs,
  headerCellsArrayReceiptCheque,
  headerCellsArrayReceiptBankTransfer,
  headerCellsArrayReceiptCreditCard,
  headerCellsArrayReceiptPaymentApp,
  headerCellsArrayReceiptPayPal,
  headerCellsArrayReceiptCash,
  headerCellsArrayReceiptOther,
  headerCellsArrayReceiptWithholdingTax,
} from '../../constants/gridColumns';
import {
  rowValuesSetCommonDocs,
  rowValuesSetCheque,
  rowValuesSetBankTransfer,
  rowValuesSetCreditCard,
  rowValuesSetPaymentApp,
  rowValuesSetPayPal,
  rowValuesSetCash,
  rowValuesSetOther,
  rowValuesSetWithholdingTax,
} from '../../constants/valuesSets';
import {
  rowFieldsSetCommonDocs,
  rowFieldsSetCheque,
  rowFieldsSetBankTransfer,
  rowFieldsSetCreditCard,
  rowFieldsSetPaymentApp,
  rowFieldsSetPayPal,
  rowFieldsSetCash,
  rowFieldsSetOther,
  rowFieldsSetWithholdingTax,
} from '../../constants/fieldSets';
import ButtonAction from '../../../_common/components/ButtonAction';
import styles from './styles.module.scss';

const GridEditableRows = ({gridType, containerClassnames = ''}) => {
  const {formikCreateDocument} = useCustomerDetailsUIContext();
  const {values} = formikCreateDocument;

  const gridData = (gridType) => {
    switch (gridType) {
      case G.CHEQUE:
        return {
          headerCells: headerCellsArrayReceiptCheque,
          valuesSet: rowValuesSetCheque,
          fieldsSet: rowFieldsSetCheque,
          fieldRowName: 'rowsCheque',
          values: values.rowsCheque,
          styles: styles.headerCheque
        };
      case G.BANK_TRANSFER:
        return {
          headerCells: headerCellsArrayReceiptBankTransfer,
          valuesSet: rowValuesSetBankTransfer,
          fieldsSet: rowFieldsSetBankTransfer,
          fieldRowName: 'rowsBankTransfer',
          values: values.rowsBankTransfer,
          styles: styles.headerBankTransfer
        };
      case G.CREDIT_CARD:
        return {
          headerCells: headerCellsArrayReceiptCreditCard,
          valuesSet: rowValuesSetCreditCard,
          fieldsSet: rowFieldsSetCreditCard,
          fieldRowName: 'rowsCreditCard',
          values: values.rowsCreditCard,
          styles: styles.headerCreditCard
        };
      case G.PAYMENT_APP:
        return {
          headerCells: headerCellsArrayReceiptPaymentApp,
          valuesSet: rowValuesSetPaymentApp,
          fieldsSet: rowFieldsSetPaymentApp,
          fieldRowName: 'rowsPaymentApp',
          values: values.rowsPaymentApp,
          styles: styles.headerPaymentApp
        };
      case G.PAY_PAL:
        return {
          headerCells: headerCellsArrayReceiptPayPal,
          valuesSet: rowValuesSetPayPal,
          fieldsSet: rowFieldsSetPayPal,
          fieldRowName: 'rowsPayPal',
          values: values.rowsPayPal,
          styles: styles.headerPayPal
        };
      case G.CASH:
        return {
          headerCells: headerCellsArrayReceiptCash,
          valuesSet: rowValuesSetCash,
          fieldsSet: rowFieldsSetCash,
          fieldRowName: 'rowsCash',
          values: values.rowsCash,
          styles: styles.headerCash
        };
      case G.OTHER:
        return {
          headerCells: headerCellsArrayReceiptOther,
          valuesSet: rowValuesSetOther,
          fieldsSet: rowFieldsSetOther,
          fieldRowName: 'rowsOther',
          values: values.rowsOther,
          styles: styles.headerOther
        };
      case G.WITHHOLDING_TAX:
        return {
          headerCells: headerCellsArrayReceiptWithholdingTax,
          valuesSet: rowValuesSetWithholdingTax,
          fieldsSet: rowFieldsSetWithholdingTax,
          fieldRowName: 'rowsWithholdingTax',
          values: values.rowsWithholdingTax,
          styles: styles.headerWithholdingTax
        };
      case G.COMMON_DOCS:
      default:
        return {
          headerCells: headerCellsArrayCommonDocs,
          valuesSet: rowValuesSetCommonDocs,
          fieldsSet: rowFieldsSetCommonDocs,
          fieldRowName: 'rowsCommonDocs',
          values: values.rowsCommonDocs,
          styles: styles.headerCommonDocs
        };
    }
  };

  const mapHeaderCells = (item) => {
    return (
      <div className={classnames(styles.headerCell, item.customItemStyles)}>
        {item.label}
        {item.showMarkNecessary && <span className="text-danger">*</span>}
      </div>
    );
  };

  return (
    <div className={classnames(styles.gridEditableRows, containerClassnames)}>
      <div className={classnames(styles.header, gridData(gridType).styles)}>
        {gridData(gridType).headerCells.map(mapHeaderCells)}
      </div>
      <FormikProvider value={formikCreateDocument}>
        <FieldArray name={gridData(gridType).fieldRowName}>
          {({remove, push}) => {
            return (
              <>
                <div className={styles.gridBody}>
                  {(gridData(gridType).values.length > 0) && gridData(gridType).values.map((sets, index) => (
                    <EditableRow
                      gridType={gridType}
                      fieldsSet={(gridData(gridType).fieldsSet)(index)}
                      values={values}
                      removeRow={() => remove(index)}
                      index={index}
                    />
                  ))
                  }
                </div>
                <ButtonAction
                  variant={'link'}
                  onClick={() => push(gridData(gridType).valuesSet)}
                  label={
                    <FormattedMessage
                      id="customer-details-popup-create-document-button-label-addRow"
                      defaultMessage="Add row"
                    />
                  }
                  customClassnames={styles.buttonAddRow}
                  avoidDoubleClick={false}
                />
              </>
            );
          }}
        </FieldArray>
      </FormikProvider>
    </div>
  );
};

export default GridEditableRows;